import { collectMarkdownCodeRanges, parseMarkdownAst } from "./markdown-parser";

export type BodyCommentKind = "line" | "html";

export type BodySourceToken = {
  kind: "text" | "comment";
  commentKind?: BodyCommentKind;
  value: string;
  start: number;
  end: number;
};

export type BodyCommentIssue = {
  code: "unclosed" | "stray_close" | "nested";
  message: string;
  index: number;
};

export type BodySourceAnalysis = {
  tokens: BodySourceToken[];
  issues: BodyCommentIssue[];
};

export type BodyLineCommentToggleResult = {
  value: string;
  selectionStart: number;
  selectionEnd: number;
};

export type BodyProtectedRange = {
  start: number;
  end: number;
};

/**
 * Tokenize authoring-only body comments without treating URL slashes as comments.
 * Whole-line comments include their terminating newline so removing them is
 * semantically transparent to lists and adjacent Markdown blocks.
 */
export function analyzeBodySource(source: string, protectedRanges?: BodyProtectedRange[]): BodySourceAnalysis {
  const effectiveProtectedRanges = [...(protectedRanges ?? collectMarkdownCodeRanges(parseMarkdownAst(source)))]
    .sort((left, right) => left.start - right.start || left.end - right.end);
  const tokens: BodySourceToken[] = [];
  const issues: BodyCommentIssue[] = [];
  let index = 0;
  let textStart = 0;
  let protectedRangeIndex = 0;

  const pushText = (end: number) => {
    if (end <= textStart) return;
    tokens.push({ kind: "text", value: source.slice(textStart, end), start: textStart, end });
  };

  const pushComment = (start: number, end: number, commentKind: BodyCommentKind) => {
    pushText(start);
    tokens.push({ kind: "comment", commentKind, value: source.slice(start, end), start, end });
    index = end;
    textStart = end;
  };

  while (index < source.length) {
    while (effectiveProtectedRanges[protectedRangeIndex]?.end <= index) protectedRangeIndex += 1;
    const protectedRange = effectiveProtectedRanges[protectedRangeIndex];
    if (protectedRange && protectedRange.start <= index && protectedRange.end > index) {
      index = Math.max(index + 1, protectedRange.end);
      continue;
    }
    if (isLineStart(source, index)) {
      const contentStart = skipHorizontalWhitespace(source, index);
      if (isLineCommentStart(source, contentStart)) {
        pushComment(index, lineEndIncludingBreak(source, contentStart), "line");
        continue;
      }

      if (source.startsWith("<!--", contentStart)) {
        const closeStart = source.indexOf("-->", contentStart + 4);
        if (closeStart === -1) {
          issues.push({ code: "unclosed", message: "正文注释缺少结束标记 -->", index: contentStart });
          pushComment(index, source.length, "html");
          continue;
        }

        const nestedStart = source.indexOf("<!--", contentStart + 4);
        if (nestedStart !== -1 && nestedStart < closeStart) {
          issues.push({ code: "nested", message: "正文注释不支持嵌套 <!-- -->", index: nestedStart });
        }

        const closeEnd = closeStart + 3;
        const suffixEnd = skipHorizontalWhitespace(source, closeEnd);
        if (suffixEnd === source.length || source[suffixEnd] === "\n" || source[suffixEnd] === "\r") {
          pushComment(index, lineEndIncludingBreak(source, suffixEnd), "html");
          continue;
        }
      }
    }

    if (source[index] === "`") {
      const codeSpanEnd = findInlineCodeSpanEnd(source, index);
      if (codeSpanEnd !== null) {
        index = codeSpanEnd;
        continue;
      }
    }

    if (source.startsWith("<!--", index)) {
      const closeStart = source.indexOf("-->", index + 4);
      if (closeStart === -1) {
        issues.push({ code: "unclosed", message: "正文注释缺少结束标记 -->", index });
        pushComment(index, source.length, "html");
        continue;
      }

      const nestedStart = source.indexOf("<!--", index + 4);
      if (nestedStart !== -1 && nestedStart < closeStart) {
        issues.push({ code: "nested", message: "正文注释不支持嵌套 <!-- -->", index: nestedStart });
      }

      pushComment(index, closeStart + 3, "html");
      continue;
    }

    if (source.startsWith("-->", index)) {
      issues.push({ code: "stray_close", message: "正文注释缺少开始标记 <!--", index });
      index += 3;
      continue;
    }

    index += 1;
  }

  pushText(source.length);
  return { tokens, issues };
}

export function stripBodyComments(source: string, protectedRanges?: BodyProtectedRange[]): string {
  return analyzeBodySource(source, protectedRanges).tokens
    .filter((token) => token.kind === "text")
    .map((token) => token.value)
    .join("");
}

export function getPublicBodyMarkdown(source: string, protectedRanges?: BodyProtectedRange[]): string {
  return stripBodyComments(source, protectedRanges)
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function isBodyCommentOnly(source: string): boolean {
  return Boolean(source.trim()) && !getPublicBodyMarkdown(source);
}

/** Toggle preferred line comments for the current line or all selected nonblank lines. */
export function toggleBodyLineComments(source: string, rawStart: number, rawEnd: number): BodyLineCommentToggleResult {
  const start = Math.max(0, Math.min(source.length, rawStart));
  const end = Math.max(start, Math.min(source.length, rawEnd));
  const effectiveEnd = end > start && source[end - 1] === "\n" ? end - 1 : end;
  const lineStart = start === 0 ? 0 : source.lastIndexOf("\n", start - 1) + 1;
  const nextBreak = source.indexOf("\n", effectiveEnd);
  const lineEnd = nextBreak === -1 ? source.length : nextBreak;
  const lines = source.slice(lineStart, lineEnd).split("\n");
  const contentLines = lines.filter((line) => line.trim());
  const shouldUncomment = contentLines.length > 0 && contentLines.every((line) => /^\s*\/\/(?:\s|$)/.test(line));
  const transformed = lines.map((line) => {
    if (!line.trim()) return line;
    if (shouldUncomment) return line.replace(/^(\s*)\/\/(?:\s?)/, "$1");
    const indentation = line.match(/^\s*/)?.[0] ?? "";
    return `${indentation}// ${line.slice(indentation.length)}`;
  }).join("\n");

  return {
    value: `${source.slice(0, lineStart)}${transformed}${source.slice(lineEnd)}`,
    selectionStart: lineStart,
    selectionEnd: lineStart + transformed.length
  };
}

function isLineStart(source: string, index: number): boolean {
  return index === 0 || source[index - 1] === "\n" || source[index - 1] === "\r";
}

function skipHorizontalWhitespace(source: string, start: number): number {
  let index = start;
  while (source[index] === " " || source[index] === "\t") index += 1;
  return index;
}

function isLineCommentStart(source: string, index: number): boolean {
  if (!source.startsWith("//", index)) return false;
  const next = source[index + 2];
  return next === undefined || next === " " || next === "\t" || next === "\n" || next === "\r";
}

function lineEndIncludingBreak(source: string, start: number): number {
  let index = start;
  while (index < source.length && source[index] !== "\n" && source[index] !== "\r") index += 1;
  if (source[index] === "\r" && source[index + 1] === "\n") return index + 2;
  return index < source.length ? index + 1 : source.length;
}

/**
 * Return the position immediately after a matching Markdown code-span
 * delimiter. A delimiter only closes a span when its backtick run has the
 * same length as the opening run, matching CommonMark code-span behavior.
 */
function findInlineCodeSpanEnd(source: string, start: number): number | null {
  const delimiterLength = countBacktickRun(source, start);
  let index = start + delimiterLength;

  while (index < source.length) {
    const nextBacktick = source.indexOf("`", index);
    if (nextBacktick === -1) return null;

    const candidateLength = countBacktickRun(source, nextBacktick);
    if (candidateLength === delimiterLength) return nextBacktick + candidateLength;
    index = nextBacktick + candidateLength;
  }

  return null;
}

function countBacktickRun(source: string, start: number): number {
  let end = start;
  while (source[end] === "`") end += 1;
  return end - start;
}
